'use no memo';

import { type AiResponse, AiResponseReport, Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  CardContent,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { DownloadIcon, EyeOpenIcon, FileTextIcon } from '@radix-ui/react-icons';
import {
  type ColumnDef,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import debounce from 'lodash/debounce';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';

import { ListRow, ListRows } from '../../../../components/ListRow';
import { getLoadingColumns } from '../../../../components/utils';
import { FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME, ROWS_PER_PAGE } from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';
import { useAiResponseQuery } from '../../../../hooks/react-query/useAiResponseQuery';
import { OpenAIClientService } from '../../../../tools/helpers';
import { BuildPdfModal } from './BuildPdfModal';
import { MemoReportForm } from './MemoReportDialog';
import { MemoSummaryFilter } from './MemoSummaryFilter';
import {
  getDefaultMemoSummarySearchFields,
  type MemoSummarySearchSchemeType,
  useMemoSummarySearchForm,
} from './MemoSummaryUtils';

const loadingResults = Array.from({ length: 5 }, () => ({}) as AiResponse);

export interface MemoSummaryListProps {
  /** Phones: told when a saved report opens or closes in place of the list. */
  onReportOpenChange?: (isOpen: boolean) => void;
  /** The project's prime job, preselected as the PDF's job number. */
  primaryEventId?: number;
  /** When true, `startDate` / `endDate` are read-only and driven by `parentDateRange`. */
  datesControlledExternally?: boolean;
  /** Used when `datesControlledExternally` is true (e.g. Job Note filter dates as `yyyy-MM-dd`). */
  parentDateRange?: {
    startDate?: string;
    endDate?: string;
  };
  /** Phones: reports as rows with labelled actions, and a report opens in place of the list. */
  mobileLayout?: boolean;
}

/** AI report status as a token tint pair. Only a failed report is a problem. */
const reportStatusPillClass = (status: string | undefined) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-success text-success-foreground';
    case 'failed':
      return 'bg-danger text-danger-foreground';
    case 'pending':
      return 'bg-info text-info-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

/** Opens the built PDF of a report in a new tab. */
const downloadPdf = async (aiResponseId: number) => {
  const batch = await OpenAIClientService.BatchGetAiResponseReport?.(
    AiResponseReport.create({ aiResponsesId: aiResponseId }),
  );
  const reportId = batch?.results?.[0]?.id;
  if (!reportId) return;
  const urlObj = await OpenAIClientService.DownloadAiResponseReport?.(
    AiResponseReport.create({ id: reportId }),
  );
  if (urlObj?.url) window.open(urlObj.url, '_blank');
};

const formatReportDate = (createdAt: string | undefined, pattern: string) => {
  if (!createdAt) return 'N/A';
  try {
    return format(parseISO(createdAt), pattern);
  } catch {
    return createdAt;
  }
};

export const MemoSummaryList: FC<MemoSummaryListProps> = ({
  onReportOpenChange,
  primaryEventId,
  datesControlledExternally = false,
  parentDateRange,
  mobileLayout = false,
}) => {
  const { user } = useAuth();
  const loggedUserId = user.id;
  const [selectedAiResponse, setSelectedAiResponse] = useState<AiResponse | null>(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [buildPdfAiResponse, setBuildPdfAiResponse] = useState<AiResponse | null>(null);
  const [isBuildPdfModalOpen, setIsBuildPdfModalOpen] = useState(false);
  const [hasBuiltPdf, setHasBuiltPdf] = useState<Record<number, boolean>>({});
  const isViewingReport = mobileLayout && !!selectedAiResponse && isReportDialogOpen;
  useEffect(() => {
    onReportOpenChange?.(isViewingReport);
  }, [isViewingReport, onReportOpenChange]);

  const canBuildPdf = useMemo(
    () => user.permissionGroups.some((p) => p.name === 'DevTesting'),
    [user.permissionGroups],
  );

  const formDefaultValues = useMemo(() => {
    if (!datesControlledExternally || !parentDateRange) {
      return getDefaultMemoSummarySearchFields();
    }
    return getDefaultMemoSummarySearchFields({
      startDate: parentDateRange.startDate,
      endDate: parentDateRange.endDate,
    });
  }, [datesControlledExternally, parentDateRange]);

  const form = useMemoSummarySearchForm({
    defaultValues: formDefaultValues,
  });

  const [filter, setFilter] = useState<MemoSummarySearchSchemeType>(form.getValues());
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'id', desc: true }]);
  const debouncedFilterRef = useRef<ReturnType<typeof debounce> | undefined>(undefined);

  useEffect(() => {
    debouncedFilterRef.current = debounce((data: MemoSummarySearchSchemeType) => {
      setFilter(data);
      setPage(0);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);

    const subscription = form.watch((formData) => {
      if (debouncedFilterRef.current) {
        debouncedFilterRef.current(formData as MemoSummarySearchSchemeType);
      }
    });

    return () => {
      subscription.unsubscribe();
      debouncedFilterRef.current?.cancel();
    };
  }, [form]);

  useEffect(() => {
    if (!datesControlledExternally || !parentDateRange) return;
    form.setValue('startDate', parentDateRange.startDate, { shouldDirty: false });
    form.setValue('endDate', parentDateRange.endDate, { shouldDirty: false });
  }, [datesControlledExternally, form, parentDateRange]);

  // Reset page when sorting changes
  useEffect(() => {
    setPage(0);
  }, [sorting]);

  const queryFilters = useMemo(() => {
    const filters: Record<string, unknown> = {
      createdByUserId: loggedUserId,
      isActive: true,
      pageNumber: page,
    };

    if (filter.startDate && filter.endDate) {
      filters.dateRange = ['>=', filter.startDate, '<', filter.endDate];
      filters.dateTarget = ['created_at', 'created_at'];
    } else if (filter.startDate) {
      filters.dateRange = ['>=', filter.startDate];
      filters.dateTarget = ['created_at'];
    } else if (filter.endDate) {
      filters.dateRange = ['<', filter.endDate];
      filters.dateTarget = ['created_at'];
    }

    if (filter.response) {
      filters.response = `%${filter.response}%`;
    }

    // Add sorting
    if (sorting.length > 0) {
      const sort = sorting[0];
      filters.orderBy = sort.id;
      filters.orderDir = sort.desc ? 'DESC' : 'ASC';
    }

    return filters;
  }, [loggedUserId, filter, page, sorting]);

  const aiResponseQuery = useAiResponseQuery({
    filters: queryFilters,
    enabled: loggedUserId !== 0,
  });

  const totalCount = aiResponseQuery.data?.totalCount ?? 0;
  const results = aiResponseQuery.data?.results ?? [];

  const columns = useMemo<ColumnDef<AiResponse>[]>(
    () => [
      {
        id: 'reportType',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Report Type" column={column} />
        ),
        cell: () => <span>Job Summary</span>,
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: true,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Status" column={column} />
        ),
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${reportStatusPillClass(status)}`}
            >
              {status || 'Unknown'}
            </span>
          );
        },
      },
      {
        id: 'created_at',
        accessorKey: 'createdAt',
        enableSorting: true,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Date Completed" column={column} />
        ),
        cell: ({ row }) => {
          return formatReportDate(row.original.createdAt, 'EEEE, MMMM d, yyyy, h:mm a');
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Actions" column={column} />
        ),
        cell: ({ row }) => {
          const aiResponse = row.original;
          const aid = aiResponse.id ?? 0;
          const showDownload = hasBuiltPdf[aid];
          const isCompleted = aiResponse.status?.toLowerCase() === 'completed';
          return (
            <div
              className={`flex items-center gap-0${isCompleted ? '' : ' pointer-events-none opacity-50'}`}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-accent"
                      disabled={!isCompleted}
                      aria-label="View Report"
                      onClick={() => {
                        setSelectedAiResponse(aiResponse);
                        setIsReportDialogOpen(true);
                      }}
                    >
                      <EyeOpenIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View Report</p>
                  </TooltipContent>
                </Tooltip>
                {canBuildPdf ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:bg-accent"
                        disabled={!isCompleted}
                        onClick={() => {
                          setBuildPdfAiResponse(aiResponse);
                          setIsBuildPdfModalOpen(true);
                        }}
                      >
                        <FileTextIcon className="mr-2 h-4 w-4" />
                        <span>Build PDF</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Build PDF</p>
                    </TooltipContent>
                  </Tooltip>
                ) : null}
                {showDownload && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:bg-accent"
                        disabled={!isCompleted}
                        onClick={() => void downloadPdf(aid)}
                        aria-label="Download PDF"
                      >
                        <DownloadIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download PDF</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          );
        },
      },
    ],
    [hasBuiltPdf, canBuildPdf],
  );

  const loadingColumns = useMemo(() => getLoadingColumns(columns), [columns]);

  const table = useReactTable({
    data: aiResponseQuery.isLoading ? loadingResults : results,
    columns: aiResponseQuery.isLoading ? loadingColumns : columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: true,
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / ROWS_PER_PAGE),
    state: {
      sorting,
      pagination: {
        pageIndex: page,
        pageSize: ROWS_PER_PAGE,
      },
    },
    onSortingChange: setSorting,
  });

  const pageCount = Math.ceil(totalCount / ROWS_PER_PAGE) || 1;

  const buildPdfModal = buildPdfAiResponse && (
    <BuildPdfModal
      aiResponse={buildPdfAiResponse}
      primaryEventId={primaryEventId}
      open={isBuildPdfModalOpen}
      onOpenChange={(open) => {
        setIsBuildPdfModalOpen(open);
        if (!open) setBuildPdfAiResponse(null);
      }}
      onPdfBuilt={() => {
        const aid = buildPdfAiResponse.id ?? 0;
        if (aid) {
          setHasBuiltPdf((prev) => ({ ...prev, [aid]: true }));
        }
      }}
    />
  );

  if (mobileLayout) {
    // A report opens in place of the list, so nothing stacks on top of the sheet.
    if (selectedAiResponse && isReportDialogOpen) {
      const openReportId = selectedAiResponse.id ?? 0;
      return (
        <>
          <MemoReportForm
            memoRequest={Memo.create({ idList: selectedAiResponse.memoIds || '' })}
            aiResponse={selectedAiResponse}
            enabled
            closeLabel="Back to reports"
            onClose={() => {
              setIsReportDialogOpen(false);
              setSelectedAiResponse(null);
            }}
            onBuildPdf={
              canBuildPdf
                ? () => {
                    setBuildPdfAiResponse(selectedAiResponse);
                    setIsBuildPdfModalOpen(true);
                  }
                : undefined
            }
            onDownloadPdf={
              hasBuiltPdf[openReportId] ? () => void downloadPdf(openReportId) : undefined
            }
          />
          {buildPdfModal}
        </>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <Form {...form}>
          <MemoSummaryFilter
            compact
            datesControlledExternally={datesControlledExternally}
            preservedDateRange={parentDateRange}
          />
        </Form>
        {aiResponseQuery.isLoading ? (
          <p role="status" className="text-muted-foreground text-sm">
            Loading reports...
          </p>
        ) : results.length === 0 ? (
          <p className="text-muted-foreground text-sm">No reports yet.</p>
        ) : (
          // One row per report: what it is and when. Opening it shows Copy, Build PDF and Download.
          <ListRows label="Reports" className="-mx-4">
            {results.map((aiResponse) => {
              const isCompleted = aiResponse.status?.toLowerCase() === 'completed';
              return (
                <ListRow
                  key={aiResponse.id}
                  title="Job Summary"
                  badge={isCompleted ? undefined : aiResponse.status || 'Unknown'}
                  details={formatReportDate(aiResponse.createdAt, 'MMM d, yyyy h:mm a')}
                  actionLabel="Open report"
                  disabled={!isCompleted}
                  onSelect={() => {
                    setSelectedAiResponse(aiResponse);
                    setIsReportDialogOpen(true);
                  }}
                />
              );
            })}
          </ListRows>
        )}
        {pageCount > 1 && (
          <nav aria-label="Report pages" className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous page
            </Button>
            <span className="text-muted-foreground text-sm tabular-nums">
              Page {page + 1} of {pageCount}
            </span>
            <Button
              type="button"
              variant="outline"
              disabled={page >= pageCount - 1}
              onClick={() => setPage(page + 1)}
            >
              Next page
            </Button>
          </nav>
        )}
        {buildPdfModal}
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-lg">
      {/* Top Navigation Bar */}
      <div className="border-border bg-card rounded-t-lg border-b px-4 pb-2 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-foreground text-xl font-semibold">My Reports</h1>
          </div>
          <div className="flex items-center gap-4">
            <DataTablePagination
              pageCount={Math.ceil(totalCount / ROWS_PER_PAGE) || 1}
              currentPage={page}
              setPage={setPage}
            />
            <span className="text-muted-foreground text-sm">Total Entries: {totalCount}</span>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="border-border border-b px-4 py-2">
        <Form {...form}>
          <MemoSummaryFilter
            datesControlledExternally={datesControlledExternally}
            preservedDateRange={parentDateRange}
          />
        </Form>
      </div>

      {/* Table Section */}
      <div className="bg-card [&_tbody_tr:hover]:bg-accent [&_tbody_tr]:bg-card [&_thead]:bg-muted [&_thead_th]:text-foreground [&_thead_th_button]:text-foreground flex-1 overflow-auto rounded-b-lg [&_thead_th>div]:text-[14px] [&_thead_th]:text-[14px] [&_thead_th]:font-medium [&_thead_th_button]:text-[14px] [&_thead_th_button]:font-medium">
        <Card>
          <CardContent className="p-0">
            <DataTable table={table} />
          </CardContent>
        </Card>
      </div>

      {/* Report Dialog */}
      {selectedAiResponse && (
        <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
          <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>View Report</DialogTitle>
            </DialogHeader>
            <MemoReportForm
              memoRequest={Memo.create({
                idList: selectedAiResponse.memoIds || '',
              })}
              aiResponse={selectedAiResponse}
              enabled={isReportDialogOpen}
              onClose={() => {
                setIsReportDialogOpen(false);
                setSelectedAiResponse(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {buildPdfModal}
    </div>
  );
};
